import React from 'react';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import flatten from 'flat';
import { History } from 'history';
import { getCLS, getFCP, getFID, getLCP, getTTFB, Metric } from 'web-vitals';

/**
 * The theme state
 * */
type AppInsightsState = {
    error: (message: string, e: Error) => void;
    warn: (message: string, e: Error | null) => void;
    info: (message: string) => void;
};

/**
 * The default state
 * */
const defaultState: AppInsightsState = {
    error: () => {},
    warn: () => {},
    info: () => {},
};

// Create a context
const AppInsightsContext = React.createContext<AppInsightsState>(defaultState);

/*
 * Props for AppInsightsProvider
 * */
type AppInsightsProviderProps = {
    children: JSX.Element;
    instrumentationKey: string;
    history: History;
};

/**
 * This provider will be used to create the insights instance, and expose methods to create logs
 * @param props
 */
function AppInsightsProvider(props: AppInsightsProviderProps) {
    const [appInsights, setAppInsights] = React.useState<ApplicationInsights | null>(null);

    React.useEffect(() => {
        if (!props.history) {
            throw new Error('Could not initialize Telemetry Service');
        }

        if ((props.instrumentationKey?.length ?? 0) === 0) {
            throw new Error('Instrumentation key not provided');
        }

        const reactPlugin = new ReactPlugin();

        const appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: props.instrumentationKey,
                maxBatchInterval: 0,
                disableFetchTracking: false,
                extensions: [reactPlugin],
                extensionConfig: {
                    [reactPlugin.identifier]: {
                        history: props.history,
                    },
                },
            },
        });

        appInsights.loadAppInsights();

        setAppInsights(appInsights);
    }, [props.instrumentationKey]);

    React.useEffect(() => {
        if (appInsights === null) {
            return;
        }

        // wire up the web-vitals performance entries
        const onPerformanceEntry = (metric: Metric) => {
            if (!appInsights) {
                console.warn('AppInsightsController has not been initialised and will not log to Application Insights. Make sure the key is provided');
                return;
            }

            appInsights.trackMetric({ name: `${metric.id}:${metric.name}`, average: metric.value });
        };

        getCLS(onPerformanceEntry);
        getFID(onPerformanceEntry);
        getFCP(onPerformanceEntry);
        getLCP(onPerformanceEntry);
        getTTFB(onPerformanceEntry);
    }, [appInsights]);

    const error = React.useCallback((message: string, e: Error) => {
        handleLog(message, e, SeverityLevel.Error);
    }, []);

    const warn = React.useCallback((message: string, e: Error | null = null) => {
        handleLog(message, e, SeverityLevel.Warning);
    }, []);

    const info = React.useCallback((message: string) => {
        handleLog(message, null, SeverityLevel.Information);
    }, []);

    const handleLog = (message: string, e: Error | null = null, severityLevel: SeverityLevel) => {
        try {
            const data = getFlattenedData(e);

            // log to console
            switch (severityLevel) {
                case SeverityLevel.Error:
                case SeverityLevel.Critical:
                    console.error(message, data);
                    break;
                case SeverityLevel.Warning:
                    console.warn(message, data);
                    break;
                case SeverityLevel.Information:
                    console.info(message);
                    break;
                default:
                    console.log(message, data);
                    break;
            }

            // log to analytics
            return track(message, e, severityLevel, data);
        } catch (e) {
            console.error('Failed to log', e);
        }
    };

    const getFlattenedData = (e: Error | null) => {
        let data: any = {};

        if (e) {
            data = flatten(JSON.parse(JSON.stringify(e)));
        }

        data.message = e?.message;
        data.name = e?.name;
        data.stack = e?.stack;

        Object.keys(data).forEach((i) => {
            if (data[i] && !isFunction(data[i])) {
                data[i] = data[i].toString();
            } else {
                delete data[i];
            }
        });

        return data;
    };

    const isFunction = (functionToCheck: any) => functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';

    const track = (message: string, exception: Error | null = null, severityLevel: SeverityLevel, data: any) => {
        if (!appInsights) {
            console.warn('AppInsightsController has not been initialised and will not log to Application Insights. Make sure the key is provided');
            return;
        }

        if (exception) {
            appInsights.trackException({
                exception,
                properties: {
                    ...data,
                    SeverityLevel: SeverityLevel[severityLevel],
                },
            });
        } else {
            appInsights.trackTrace({
                message,
                properties: {
                    ...data,
                    SeverityLevel: SeverityLevel[severityLevel],
                },
            });
        }
    };

    return <AppInsightsContext.Provider value={{ error, warn, info }}>{props.children}</AppInsightsContext.Provider>;
}

/**
 * This hook can be used to expose the context
 * @returns AppInsightsContext
 * */
function useAppInsights() {
    const context = React.useContext(AppInsightsContext);

    if (context === undefined) {
        throw new Error('useAppInsights must be used within a AppInsightsProvider');
    }

    return context;
}

export { AppInsightsProvider, useAppInsights };
